import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Post from './Post';
import { trackEventIfConsented } from './analyticsConsent';
import { fetchRecentPosts } from './postData';
import {
  getAuthoredLinkDestinationGroup,
  getAuthoredPage,
  matchExamplesToPosts,
  matchSupportingSetupLinks,
} from './authoredPageContent';
import { trackInternalNavigationIfConsented, usePageGroupTracking } from './pageAnalytics';
import { parseListTags } from './setupContent';
import { seoSiteConfig, usePageSeo } from './seo';

const InternalOrAnchorLink = ({ link, sourcePageGroup, linkContext, className = '' }) => {
  if (!link) {
    return null;
  }

  if (link.href) {
    return (
      <a
        className={className}
        href={link.href}
        onClick={() => {
          trackEventIfConsented('hero_cta_click', {
            cta_name: link.label,
            page_context: sourcePageGroup,
          });
          trackInternalNavigationIfConsented({
            link_target: link.href,
            link_context: linkContext,
            source_page_group: sourcePageGroup,
            destination_page_group: 'footer_newsletter',
          });
        }}
      >
        {link.label}
      </a>
    );
  }

  return (
    <Link
      className={className}
      to={link.to}
      onClick={() => {
        trackEventIfConsented('hero_cta_click', {
          cta_name: link.label,
          page_context: sourcePageGroup,
        });
        trackInternalNavigationIfConsented({
          link_target: link.to,
          link_context: linkContext || link.context || 'authored_link',
          source_page_group: sourcePageGroup,
          destination_page_group: getAuthoredLinkDestinationGroup(link.to),
        });
      }}
    >
      {link.label}
    </Link>
  );
};

const AuthoredPage = ({ pageType }) => {
  const params = useParams();
  const slug = pageType === 'collection' ? params.collectionSlug : params.gearSlug;
  const page = useMemo(() => getAuthoredPage({ type: pageType, slug }), [pageType, slug]);
  const [postsLoaded, setPostsLoaded] = useState(false);
  const [posts, setPosts] = useState([]);
  const structuredData = useMemo(() => {
    if (!page) {
      return [];
    }

    const pageUrl = seoSiteConfig.toAbsoluteUrl(page.path);
    const breadcrumbs = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: seoSiteConfig.siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Desk setup ideas',
        item: seoSiteConfig.toAbsoluteUrl('/desk-setup-ideas'),
      },
    ];

    if (page.topicHubPath && page.topicHubLabel) {
      breadcrumbs.push({
        '@type': 'ListItem',
        position: breadcrumbs.length + 1,
        name: page.topicHubLabel,
        item: seoSiteConfig.toAbsoluteUrl(page.topicHubPath),
      });
    }

    breadcrumbs.push({
      '@type': 'ListItem',
      position: breadcrumbs.length + 1,
      name: page.title,
      item: pageUrl,
    });

    const pageSchema = {
      '@context': 'https://schema.org',
      '@type': page.type === 'gear' ? 'Article' : 'CollectionPage',
      headline: page.title,
      name: page.title,
      description: page.description,
      url: pageUrl,
      mainEntityOfPage: pageUrl,
      ...(page.heroVisual?.src ? { image: seoSiteConfig.toAbsoluteUrl(page.heroVisual.src) } : {}),
      author: {
        '@type': 'Organization',
        name: seoSiteConfig.siteName,
        url: seoSiteConfig.siteUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: seoSiteConfig.siteName,
        url: seoSiteConfig.siteUrl,
      },
      isPartOf: {
        '@type': 'WebSite',
        name: seoSiteConfig.siteName,
        url: seoSiteConfig.siteUrl,
      },
      about: [
        {
          '@type': 'Thing',
          name: page.topicHubLabel || 'Desk setup ideas',
        },
        {
          '@type': 'Thing',
          name: page.kicker,
        },
      ],
    };

    const schemas = [
      pageSchema,
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs,
      },
    ];

    if (page.faq?.length) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: page.faq.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      });
    }

    return schemas;
  }, [page]);

  usePageSeo({
    title: page?.title || 'Desk setup ideas',
    description: page?.description || 'Browse authored 101Setups pages and discover related desk setup inspiration.',
    canonicalPath: page?.path || '/desk-setup-ideas',
    image: page?.heroVisual?.src || seoSiteConfig.defaultImage,
    robots: page ? 'index,follow' : 'noindex,nofollow',
    type: pageType === 'gear' ? 'article' : 'website',
    structuredData,
  });

  usePageGroupTracking(page?.pageGroup || 'unknown', {
    authored_slug: page?.slug || 'unknown',
    authored_type: page?.type || 'unknown',
  });

  useEffect(() => {
    let isMounted = true;

    const loadPosts = async () => {
      if (!page) {
        setPosts([]);
        setPostsLoaded(true);
        return;
      }

      setPostsLoaded(false);

      try {
        const recentPosts = await fetchRecentPosts(120);

        if (!isMounted) {
          return;
        }

        setPosts(recentPosts);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setPosts([]);
      } finally {
        if (isMounted) {
          setPostsLoaded(true);
        }
      }
    };

    loadPosts();

    return () => {
      isMounted = false;
    };
  }, [page]);

  const exampleMatches = useMemo(() => {
    if (!postsLoaded) {
      return [];
    }

    return matchExamplesToPosts(page, posts);
  }, [page, posts, postsLoaded]);
  const supportingSetupLinks = useMemo(() => {
    if (!postsLoaded) {
      return [];
    }

    return matchSupportingSetupLinks(page, posts);
  }, [page, posts, postsLoaded]);
  const showcaseEntries = useMemo(() => {
    if (!page?.showSetupShowcase) {
      return [];
    }

    if (supportingSetupLinks.length) {
      return supportingSetupLinks.slice(0, 6);
    }

    const uniqueEntries = [];
    const seen = new Set();

    exampleMatches.forEach(example => {
      if (example.matchedPost?.id && !seen.has(example.matchedPost.id)) {
        seen.add(example.matchedPost.id);
        uniqueEntries.push({
          matchedPost: example.matchedPost,
          matchedSetup: example.matchedSetup,
          title: example.setupLinkLabel || example.matchedSetup?.title,
        });
      }
    });

    return uniqueEntries.slice(0, 6);
  }, [exampleMatches, page?.showSetupShowcase, supportingSetupLinks]);

  if (!page) {
    return (
      <section className="page-shell">
        <div className="empty-state">
          <h1>Page not found</h1>
          <p>Try the main desk setup ideas hub or browse the latest setups instead.</p>
          <Link className="home-hero-link primary" to="/desk-setup-ideas">
            Go to desk setup ideas
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="page-shell authored-page">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link
          to="/"
          onClick={() =>
            trackInternalNavigationIfConsented({
              link_target: '/',
              link_context: 'authored_breadcrumb_home',
              source_page_group: page.pageGroup,
              destination_page_group: 'home',
            })
          }
        >
          Home
        </Link>
        <span>/</span>
        <Link
          to="/desk-setup-ideas"
          onClick={() =>
            trackInternalNavigationIfConsented({
              link_target: '/desk-setup-ideas',
              link_context: 'authored_breadcrumb_search_hub',
              source_page_group: page.pageGroup,
              destination_page_group: 'search_hub',
            })
          }
        >
          Desk setup ideas
        </Link>
        {page.topicHubPath ? (
          <>
            <span>/</span>
            <Link
              to={page.topicHubPath}
              onClick={() =>
                trackInternalNavigationIfConsented({
                  link_target: page.topicHubPath,
                  link_context: 'authored_breadcrumb_topic_hub',
                  source_page_group: page.pageGroup,
                  destination_page_group: 'topic_hub',
                })
              }
            >
              {page.topicHubLabel}
            </Link>
          </>
        ) : null}
      </nav>

      <header className="page-intro authored-hero">
        <p className="page-kicker">{page.kicker}</p>
        <h1>{page.title}</h1>
        {page.intro.map(paragraph => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        {page.summaryDeck ? <div className="editorial-note authored-summary-deck">{page.summaryDeck}</div> : null}
        {page.primaryActions?.length ? (
          <div className="page-actions">
            {page.primaryActions.map((link, index) => (
              <InternalOrAnchorLink
                key={link.label}
                link={link}
                sourcePageGroup={page.pageGroup}
                linkContext={link.context || `authored_primary_action_${index + 1}`}
                className={`home-hero-link${index === 0 ? ' primary' : ''}`}
              />
            ))}
          </div>
        ) : null}
        {page.heroVisual?.src ? (
          <figure className="authored-hero-visual">
            <img src={page.heroVisual.src} alt={page.heroVisual.alt} loading="eager" />
            {page.heroVisual.caption ? <figcaption>{page.heroVisual.caption}</figcaption> : null}
          </figure>
        ) : null}
      </header>

      {page.principles?.length ? (
        <div className="page-section">
          <div className="section-heading-block">
            <p className="page-kicker">Core principles</p>
            <h2>What matters on this page</h2>
          </div>
          <div className="content-grid-two">
            {page.principles.map(item => (
              <article className="content-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {exampleMatches.length ? (
        <div className="page-section">
          <div className="section-heading-block">
            <p className="page-kicker">{page.examplesSectionKicker || 'Real setup examples'}</p>
            <h2>{page.examplesSectionTitle}</h2>
            {page.examplesSectionIntro ? <p>{page.examplesSectionIntro}</p> : null}
          </div>
          <div className="editorial-example-grid">
            {exampleMatches.map(example => (
              <article className="editorial-example-card" key={example.title}>
                <h3>{example.title}</h3>
                <p className="editorial-example-takeaway">{example.takeaway}</p>
                <p>{example.body}</p>
                <p className="editorial-example-meta">
                  <strong>Visible tactic:</strong> {example.visibleTactic}
                </p>
                {example.matchedSetup ? (
                  <Link
                    className="inline-text-link"
                    to={example.matchedSetup.path}
                    onClick={() =>
                      trackInternalNavigationIfConsented({
                        link_target: example.matchedSetup.path,
                        link_context: 'authored_example_setup_link',
                        source_page_group: page.pageGroup,
                        destination_page_group: 'setup_page',
                        post_id: example.matchedPost?.id,
                      })
                    }
                  >
                    Open related setup: {example.setupLinkLabel || example.matchedSetup.title} →
                  </Link>
                ) : example.unmatchedMessage ? (
                  <p className="editorial-example-meta">{example.unmatchedMessage}</p>
                ) : example.suppressUnmatchedSetupNote || page.suppressUnmatchedExampleNotes ? null : (
                  <p className="editorial-example-meta">Related setup selection still needs editorial review.</p>
                )}
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {showcaseEntries.length ? (
        <div className="page-section">
          <div className="section-heading-block">
            <p className="page-kicker">{page.showcaseSectionKicker || 'Setup pages'}</p>
            <h2>{page.showcaseSectionTitle || 'Open the real setups behind the guidance'}</h2>
            {page.showcaseSectionIntro ? <p>{page.showcaseSectionIntro}</p> : null}
          </div>
          <div className="post-list-grid compact-grid">
            {showcaseEntries.map(entry => {
              const model = entry.matchedPost;
              const matchedSetup = entry.matchedSetup;

              return (
                <Post
                  key={model.id}
                  id={model.id}
                  userID={model.userid}
                  imgURL={model.imgURL}
                  listTags={parseListTags(model.listtags)}
                  title={entry.title || matchedSetup?.title}
                  summary={entry.description || matchedSetup?.excerpt}
                  categoryLabel={matchedSetup?.primaryCategory?.shortLabel || page.topicHubLabel}
                  canonicalPath={matchedSetup?.path}
                  sharePath={matchedSetup?.path}
                  altText={matchedSetup?.altText}
                  pageContext={page.pageGroup}
                />
              );
            })}
          </div>
        </div>
      ) : null}

      {page.midPageLinks?.length ? (
        <div className="page-section">
          <div className="section-heading-block">
            <p className="page-kicker">{page.midPageLinksKicker || 'Related next pages'}</p>
            <h2>{page.midPageLinksTitle || 'Open the next practical page'}</h2>
            {page.midPageLinksIntro ? <p>{page.midPageLinksIntro}</p> : null}
          </div>
          <div className="category-grid">
            {page.midPageLinks.map(link => (
              <Link
                key={link.to}
                className="category-card"
                to={link.to}
                onClick={() =>
                  trackInternalNavigationIfConsented({
                    link_target: link.to,
                    link_context: 'authored_mid_page_related_link',
                    source_page_group: page.pageGroup,
                    destination_page_group: getAuthoredLinkDestinationGroup(link.to),
                  })
                }
              >
                <p className="category-label">{link.label || 'Related page'}</p>
                <h3>{link.title}</h3>
                <p>{link.description}</p>
                <span className="category-cta">Open page →</span>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      {page.comparisonRows?.length ? (
        <div className="page-section">
          <div className="section-heading-block">
            <p className="page-kicker">Comparison</p>
            <h2>{page.comparisonTitle}</h2>
          </div>
          <div className="comparison-table-wrap">
            <table className="comparison-table">
              <thead>
                <tr>
                  {page.comparisonColumns.map(column => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.comparisonRows.map(row => (
                  <tr key={row.join('|')}>
                    {row.map(cell => (
                      <td key={cell}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {page.supportingSections?.map(section => (
        <div className="page-section" key={section.title}>
          <div className="section-heading-block">
            <p className="page-kicker">Supporting guidance</p>
            <h2>{section.title}</h2>
          </div>
          <div className="content-grid-two">
            {section.items.map(item => (
              <article className="content-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      ))}

      {page.checklist?.length ? (
        <div className="page-section">
          <div className="section-heading-block">
            <p className="page-kicker">Checklist</p>
            <h2>{page.checklistTitle}</h2>
          </div>
          <article className="content-card authored-checklist-card">
            <ol className="authored-checklist">
              {page.checklist.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </article>
        </div>
      ) : null}

      {page.faq?.length ? (
        <div className="page-section">
          <div className="section-heading-block">
            <p className="page-kicker">FAQ</p>
            <h2>Quick answers</h2>
          </div>
          <div className="content-grid-two">
            {page.faq.map(item => (
              <article className="content-card" key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {page.relatedLinks?.length ? (
        <div className="page-section">
          <div className="section-heading-block">
            <p className="page-kicker">Explore next</p>
            <h2>Keep moving through the setup graph</h2>
          </div>
          <div className="category-grid">
            {page.relatedLinks.map(link => (
              <Link
                key={link.to}
                className="category-card"
                to={link.to}
                onClick={() =>
                  trackInternalNavigationIfConsented({
                    link_target: link.to,
                    link_context: 'authored_related_link',
                    source_page_group: page.pageGroup,
                    destination_page_group: getAuthoredLinkDestinationGroup(link.to),
                  })
                }
              >
                <p className="category-label">Related page</p>
                <h3>{link.title}</h3>
                <p>{link.description}</p>
                <span className="category-cta">Open page →</span>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      {page.newsletterCard ? (
        <div className="page-section">
          <article className="content-card authored-newsletter-card">
            <p className="page-kicker">Newsletter</p>
            <h2>{page.newsletterCard.title}</h2>
            <p>{page.newsletterCard.body}</p>
            <InternalOrAnchorLink
              link={{ label: page.newsletterCard.ctaLabel, href: page.newsletterCard.ctaHref }}
              sourcePageGroup={page.pageGroup}
              linkContext="authored_newsletter_jump"
              className="home-hero-link primary"
            />
          </article>
        </div>
      ) : null}
    </section>
  );
};

export default AuthoredPage;
